import React, { useState, useEffect, useRef } from 'react';

const SEARCH_HISTORY_KEY = 'searchHistory';
const MAX_HISTORY_ITEMS = 20;

export default function Search ({ findKirks, initialKeyword }) {
  const [keyword, setKeyword] = useState(initialKeyword || '');
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const debounceTimeout = useRef(null);
  const hasInitialized = useRef(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const touchStartY = useRef(null);
  const isTouchingDropdown = useRef(false);

  // Load search history from localStorage
  const loadSearchHistory = () => {
    try {
      const history = JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]');
      setSearchHistory(history);
    } catch {
      setSearchHistory([]);
    }
  };

  // Save search to history
  const saveToHistory = (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return;

    try {
      let history = JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]');
      // Remove duplicate if exists
      history = history.filter(item => item !== searchTerm);
      // Add to beginning
      history.unshift(searchTerm);
      // Keep only last 10
      history = history.slice(0, MAX_HISTORY_ITEMS);
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
      setSearchHistory(history);
    } catch {
      // Ignore localStorage errors
    }
  };

  // initialKeyword (from the parent's persisted per-page state) is already the
  // authoritative source of truth across reloads, so just load history on mount.
  useEffect(() => {
    if (!hasInitialized.current) {
      loadSearchHistory();
      hasInitialized.current = true;
    }
  }, []);

  // Update keyword when initialKeyword changes (URL navigation or page switch)
  useEffect(() => {
    if (initialKeyword && initialKeyword !== keyword) {
      setKeyword(initialKeyword);
    } else if (!initialKeyword && hasInitialized.current) {
      // Clear keyword if the current page has no keyword
      setKeyword('');
      findKirks('');
    }
  }, [initialKeyword]);

  // Close history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the container (input area)
      const isInsideContainer = containerRef.current && containerRef.current.contains(event.target);
      // Check if click is inside the dropdown (which is position:fixed and may be outside container in DOM)
      const dropdown = document.querySelector('[data-search-dropdown="true"]');
      const isInsideDropdown = dropdown && dropdown.contains(event.target);

      if (!isInsideContainer && !isInsideDropdown) {
        setShowHistory(false);
      }
    };

    // Use click event for better compatibility (fires after mousedown/touchstart)
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('touchend', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('touchend', handleClickOutside, true);
    };
  }, []);

  const handleChange = event => {
    const newKeyword = event.target.value;
    setKeyword(newKeyword);

    // Debounce: clear previous timeout before setting a new one
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      findKirks(newKeyword);
      // Don't save to history here - only save on blur or Enter
    }, 500); // 500ms debounce
  };

  // Save to history when user leaves the search box (blur)
  const handleBlur = () => {
    if (keyword && keyword.trim() !== '') {
      saveToHistory(keyword);
    }
    // Delay closing to allow touch interactions on dropdown to complete
    setTimeout(() => {
      if (!isTouchingDropdown.current) {
        setShowHistory(false);
      }
    }, 100);
  };

  // Save to history when user presses Enter
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (keyword && keyword.trim() !== '') {
        saveToHistory(keyword);
      }
      setShowHistory(false);
      // Prevent form submission if inside a form
      event.preventDefault();
    }
  };

  const handleHistoryClick = (historyItem) => {
    setKeyword(historyItem);
    findKirks(historyItem);
    setShowHistory(false);
  };

  const handleFocus = () => {
    loadSearchHistory();
    // Calculate dropdown position based on input's position
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width
      });
    }
    setShowHistory(true);
  };

  // Toggle dropdown when clicking on the input (not just focus)
  const handleInputClick = (event) => {
    event.stopPropagation();
    // If already focused and dropdown is showing, toggle it off
    if (document.activeElement === inputRef.current && showHistory) {
      setShowHistory(false);
    } else {
      // Otherwise show it (this handles the case when input is already focused)
      loadSearchHistory();
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 2,
          left: rect.left,
          width: rect.width
        });
      }
      setShowHistory(true);
    }
  };

  const clearSearch = () => {
    setKeyword('');
    findKirks('');
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const isHamburgerMenu = window.innerWidth <= 1206;

  const historyDropdownStyle = {
    position: 'fixed',
    top: `${dropdownPosition.top}px`,
    left: `${dropdownPosition.left}px`,
    width: `${dropdownPosition.width}px`,
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 9999,
    maxHeight: '300px',
    overflowY: 'auto'
  };

  const historyItemStyle = {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    fontSize: '14px',
    color: '#333'
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <input
        ref={inputRef}
        className='search-input'
        type='text'
        id='keyword'
        name='search-kirk-filter'
        value={keyword}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder='Search...'
        autoComplete='off'
        autoCorrect='off'
        autoCapitalize='off'
        spellCheck='false'
        data-form-type='other'
        style={{
          paddingRight: '40px',
          width: !isHamburgerMenu
            ? `${Math.min(Math.max(keyword.length, 30), 45)}ch`
            : `${Math.min(Math.max(keyword.length, 20), 28)}ch`
        }}
        onClick={handleInputClick}
      />
      {keyword && (
        <button
          type='button'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            clearSearch();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            clearSearch();
          }}
          style={{
            position: 'absolute',
            top: '50%',
            right: '8px',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            padding: '8px',
            fontSize: '18px',
            lineHeight: '1',
            minWidth: '30px',
            minHeight: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
      )}
      {showHistory && searchHistory.length > 0 && (
        <div style={historyDropdownStyle} data-search-dropdown="true">
          {searchHistory.map((item, index) => (
            <div
              key={index}
              style={historyItemStyle}
              onMouseDown={(e) => {
                e.preventDefault();
                handleHistoryClick(item);
              }}
              onTouchStart={(e) => {
                // Record touch start position to distinguish tap from scroll
                touchStartY.current = e.touches[0].clientY;
                isTouchingDropdown.current = true;
              }}
              onTouchEnd={(e) => {
                // Only select if touch didn't move much (tap, not scroll)
                if (touchStartY.current !== null) {
                  const touchEndY = e.changedTouches[0].clientY;
                  const moved = Math.abs(touchEndY - touchStartY.current);
                  if (moved < 10) {
                    e.preventDefault();
                    handleHistoryClick(item);
                  }
                  touchStartY.current = null;
                }
                isTouchingDropdown.current = false;
              }}
              onTouchCancel={() => {
                touchStartY.current = null;
                isTouchingDropdown.current = false;
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
